import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuth } from './use-auth'

export interface InventoryItem {
  id: string
  name: string
  sku: string | null
  category: string | null
  unit: string
  unit_cost: number
  qty_on_hand: number
  reorder_point: number
  trade: string | null
  company_id: string
  archived_at: string | null
}

export interface TaskMaterial {
  id: string
  task_id: number
  item_id: string
  qty: number
  unit_cost_at_time: number
  used_at: string
  item?: { name: string; unit: string } | null
}

/** Company-wide inventory: items, stock levels, restock/adjust, low-stock. */
export function useInventory() {
  const { companyId } = useAuth()
  const qc = useQueryClient()

  const list = useQuery({
    queryKey: ['inventory'],
    queryFn: async (): Promise<InventoryItem[]> => {
      const { data, error } = await supabase
        .from('inventory_item')
        .select('id, name, sku, category, unit, unit_cost, qty_on_hand, reorder_point, trade, company_id, archived_at')
        .is('archived_at', null)
        .order('category', { ascending: true, nullsFirst: false })
        .order('name', { ascending: true })
      if (error) throw error
      return (data ?? []) as InventoryItem[]
    },
  })

  const save = useMutation({
    mutationFn: async (p: Partial<InventoryItem> & { id?: string }) => {
      const payload = {
        name: p.name,
        sku: p.sku ?? null,
        category: p.category ?? null,
        unit: p.unit ?? 'unit',
        unit_cost: p.unit_cost ?? 0,
        reorder_point: p.reorder_point ?? 0,
      }
      if (p.id) {
        const { error } = await supabase.from('inventory_item').update(payload).eq('id', p.id)
        if (error) throw error
      } else {
        // New item: seed opening stock through the ledger so qty stays audit-clean.
        const { data, error } = await supabase
          .from('inventory_item')
          .insert({ ...payload, company_id: companyId, qty_on_hand: 0 })
          .select('id')
          .single()
        if (error) throw error
        if (p.qty_on_hand && p.qty_on_hand > 0) {
          const { error: mErr } = await supabase.rpc('adjust_stock', {
            p_item: (data as { id: string }).id, p_delta: p.qty_on_hand, p_reason: 'seed', p_note: 'Opening stock',
          })
          if (mErr) throw mErr
        }
      }
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['inventory'] }),
  })

  /** Add or remove stock on hand (restock, stocktake correction, etc.). */
  const adjust = useMutation({
    mutationFn: async (input: { id: string; delta: number; reason?: string; note?: string }) => {
      const { error } = await supabase.rpc('adjust_stock', {
        p_item: input.id, p_delta: input.delta, p_reason: input.reason ?? 'restock', p_note: input.note ?? null,
      })
      if (error) throw error
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['inventory'] }),
  })

  const remove = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('inventory_item').update({ archived_at: new Date().toISOString() }).eq('id', id)
      if (error) throw error
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['inventory'] }),
  })

  const items = list.data ?? []
  const lowStock = items.filter((i) => i.reorder_point > 0 && i.qty_on_hand <= i.reorder_point)
  const stockValue = items.reduce((n, i) => n + i.qty_on_hand * i.unit_cost, 0)

  /**
   * Bulk-insert a starter pack. Opening stock is 0, so nothing goes through the adjust_stock
   * ledger: an item nobody has counted yet must not claim a quantity it does not have. The
   * operator does a stocktake and adjusts, which keeps the ledger honest.
   *
   * Unit costs are left at 0 rather than guessed, since a wrong cost quietly distorts job
   * costing and margin reports.
   */
  const importMany = useMutation({
    mutationFn: async (rows: { name: string; unit: string; category: string; reorder_point: number }[]) => {
      if (!companyId) throw new Error('No company')
      const { error } = await supabase.from('inventory_item').insert(
        rows.map((r) => ({
          name: r.name, unit: r.unit, category: r.category,
          reorder_point: r.reorder_point, unit_cost: 0, qty_on_hand: 0,
          company_id: companyId,
        })),
      )
      if (error) throw error
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['inventory'] }),
  })

  return { items, lowStock, stockValue, isLoading: list.isLoading, save, adjust, remove, importMany }
}

/** Materials logged against a single job. Feeds job cost; never billed. */
export function useTaskMaterials(taskId: number | null | undefined) {
  const qc = useQueryClient()
  const key = ['task-materials', taskId]

  const list = useQuery({
    queryKey: key,
    enabled: taskId != null,
    queryFn: async (): Promise<TaskMaterial[]> => {
      const { data, error } = await supabase
        .from('task_material')
        .select('id, task_id, item_id, qty, unit_cost_at_time, used_at, item:inventory_item(name, unit)')
        .eq('task_id', taskId!)
        .order('used_at', { ascending: true })
      if (error) throw error
      return (data ?? []) as unknown as TaskMaterial[]
    },
  })

  const log = useMutation({
    mutationFn: async (input: { itemId: string; qty: number }) => {
      const { error } = await supabase.rpc('log_task_material', { p_task: taskId, p_item: input.itemId, p_qty: input.qty })
      if (error) throw error
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: key })
      qc.invalidateQueries({ queryKey: ['inventory'] })
    },
  })

  const remove = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.rpc('remove_task_material', { p_id: id })
      if (error) throw error
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: key })
      qc.invalidateQueries({ queryKey: ['inventory'] })
    },
  })

  const materials = list.data ?? []
  const materialsCost = materials.reduce((n, m) => n + m.qty * m.unit_cost_at_time, 0)

  return { materials, materialsCost, isLoading: list.isLoading, log, remove }
}
